import { configStore, documentStore, uiStore } from "@/store/store";
import {
  BuilderElement,
  BuilderElementNestingRule,
  CustomDocumentPostTypes,
  NestingRuleType,
  PlaceToAddElementInTree
} from "@/types/elements";
import { Tree, TreeNode } from "@/types/tree";
import {
  areNodeTypesPresentInTree,
  findNode,
  findNodeParent,
  getNodeIndex,
  parentNodesReduce,
  treeNodeReduce
} from "@/lib/tree/tree";
import { POPUP_SLUG, SECTION_SLUG } from "@/lib/elements/constants";
import { left, right } from "fp-ts/lib/Either";
import { getBreakdanceBlockPostType } from "../constants/post-types";
import { isNodeDisallowedInsideWhenInlineEditingIsEnabled } from "@/lib/inline-editor/inline-editor";

type ElementNestingRuleAndChildrenRuleTypes = {
  self: BuilderElementNestingRule;
  children: NestingRuleType[];
};

function getBannedDescendantNestingTypes(
  nestingType: NestingRuleType
): NestingRuleType[] {
  /*
        note - it would be a mistake to put something here that conflicts with getNestingRuleTypesThatAreAllowedInside
        - i.e. if a container can take a "final" according to getNestingRuleTypesThatAreAllowedInside, dont say a container is
        banned to have a "final" inside it here
        */
  switch (nestingType) {
    case "container-link":
      return ["container-link", "final-link"];
    case "container-excluding-self":
      return ["container-excluding-self"];
    default:
      return [];
  }
}

function getNestingTypesFromNodeChildren(node: TreeNode): NestingRuleType[] {
  return Array.from(
    new Set( // convert array -> set -> array for uniqueness
      node.children.reduce<NestingRuleType[]>((acc, node) => {
        return [
          ...acc,
          ...treeNodeReduce<NestingRuleType[]>(
            (node, nodeTypes) => {
              return [...nodeTypes, getElementNestingRuleFromNode(node).type];
            },
            node,
            []
          )
        ];
      }, [])
    )
  );
}

function getElementFromSlug(slug: string) {
  return configStore.element(slug);
}

function getElementNestingRuleFromNode(node: TreeNode) {
  return getElementFromSlug(node.data.type).nestingRule;
}

function getBannedDescendantNestingTypesForNodeAndNodeAncestors(
  tree: Tree,
  node: TreeNode
): NestingRuleType[] {
  let _node: TreeNode | undefined = node;
  let bannedDescendantTypes: NestingRuleType[] = [];

  while (_node) {
    bannedDescendantTypes = [
      ...bannedDescendantTypes,
      ...getBannedDescendantNestingTypes(
        getElementNestingRuleFromNode(_node).type
      )
    ];
    _node = findNodeParent(tree, _node);
  }

  return bannedDescendantTypes;
}

function areNestingTypesForPotentialDescendantsBanned(
  nestingTypesForPotentialDescendants: NestingRuleType[] = [],
  bannedDescendantTypes: NestingRuleType[] = []
) {
  return nestingTypesForPotentialDescendants
    .map(type => bannedDescendantTypes.includes(type))
    .includes(true);
}

function isAllowedInside(
  tree: Tree,
  nestingRule: BuilderElementNestingRule,
  descendantTypes: NestingRuleType[],
  potentialParentNode: TreeNode
) {
  const potentialParentRules =
    getElementNestingRuleFromNode(potentialParentNode);

  if (
    !isNestingRuleAllowedInside(
      nestingRule,
      potentialParentRules,
      potentialParentNode.data.type
    )
  ) {
    return false;
  }

  const bannedDescendantTypesOfAncestors =
    getBannedDescendantNestingTypesForNodeAndNodeAncestors(
      tree,
      potentialParentNode
    );

  if (
    areNestingTypesForPotentialDescendantsBanned(
      [nestingRule.type, ...descendantTypes],
      bannedDescendantTypesOfAncestors
    )
  ) {
    return false;
  }

  if (
    nestingRule.restrictedToBeADescendantOf &&
    !doDescendantRestrictionsAllowNestingInsideOfNode(
      tree,
      potentialParentNode,
      nestingRule.restrictedToBeADescendantOf
    )
  ) {
    return false;
  }

  if (
    nestingRule.restrictedToBeADirectChildOf &&
    !nestingRule.restrictedToBeADirectChildOf.includes(
      potentialParentNode.data.type
    )
  ) {
    return false;
  }

  if (
    isNodeDisallowedInsideWhenInlineEditingIsEnabled(tree, potentialParentNode)
  ) {
    return false;
  }

  return true;
}

/**
 * thisNode is not an ID since it does not need to be in the tree
 *
 * the check is done by reference, not ID, so design library nodes with overlapping IDs or whatever... its no problem
 */
function wouldNodeBeADescendantOfItself(
  tree: Tree,
  thisNode: TreeNode,
  potentialParentNodeId: TreeNode["id"]
) {
  let currentParentNode = findNode(tree, potentialParentNodeId);

  while (currentParentNode) {
    if (currentParentNode === thisNode) return true;
    currentParentNode = findNodeParent(tree, currentParentNode);
  }

  return false;
}

function findNodeThatIsAncestorOfNode(
  tree: Tree,
  nodesToSearch: TreeNode[],
  descendantNode: TreeNode
): TreeNode | undefined {
  let foundNode: TreeNode | undefined = undefined;

  nodesToSearch.forEach(node => {
    if (isNodeADescendantOf(tree, descendantNode, node)) {
      foundNode = node;
    }
  });

  return foundNode;
}

export function isNodeADescendantOf(
  tree: Tree,
  node: TreeNode,
  possiblyParentNode: TreeNode
): boolean {
  if (node._parentId === undefined) {
    return false;
  }

  if (node._parentId === possiblyParentNode.id) {
    return true;
  }

  const parent = findNodeParent(tree, node);

  return parent ? isNodeADescendantOf(tree, parent, possiblyParentNode) : false;
}

function doDescendantRestrictionsAllowNestingInsideOfNode(
  tree: Tree,
  node: TreeNode,
  descendantRestrictions: string[]
) {
  const allParentSlugsIncludingCurrentNode = parentNodesReduce(
    (node: TreeNode, acc: string[]) => {
      return [...acc, node.data.type];
    },
    tree,
    node,
    [node.data.type]
  );

  return allParentSlugsIncludingCurrentNode.find(parentSlug =>
    descendantRestrictions.includes(parentSlug)
  );
}

/* PUBLIC API */

export function isNestingRuleAllowedInside(
  nestingRule: BuilderElementNestingRule,
  potentialParentNestingRule: BuilderElementNestingRule,
  potentialParentSlug: string,
  overrideDocumentPostType?: CustomDocumentPostTypes
) {
  const isAllowedInside = getNestingRuleTypesThatAreAllowedInside(
    potentialParentNestingRule.type,
    overrideDocumentPostType
  ).includes(nestingRule.type);

  // Override isAllowedInside for "container-restricted", in which case "restrictedToBeADirectChildOf" wins
  if (
    !isAllowedInside &&
    potentialParentNestingRule.type === "container-restricted" &&
    nestingRule.restrictedToBeADirectChildOf?.includes(potentialParentSlug)
  ) {
    return true;
  }

  // Inline block editing is a special case. We allow elements inside inline editable elements if we are in inline editing mode.
  if (potentialParentNestingRule.inlineEditable) {
    return uiStore.inlineEditingContext !== null;
  }

  return isAllowedInside;
}

/**
 * Not a pure function:
 * The nesting rules for 'root' depend on the document type being edited.
 *
 * Editing a popup? Only a popup element is allowed inside root.
 * Editing a global block? Anything can be in the root.
 * Editing a normal post / page? Only sections (and global blocks / inner content) are allowed inside root.
 *
 * This function gets the document type from the store or it can be overridden when creating elements
 * such as a Global Block through the Canvas instead of as a post.
 */
export function getNestingRuleTypesThatAreAllowedInside(
  elementRuleType: NestingRuleType,
  overrideDocumentPostType?: CustomDocumentPostTypes
): NestingRuleType[] {
  const containerNestingRule: NestingRuleType[] = [
    "container",
    "container-link",
    "final",
    "final-link",
    "container-restricted",
    "container-excluding-self",
    "final-that-is-allowed-inside-root"
  ];

  const documentPostType =
    overrideDocumentPostType || documentStore.document.documentMeta.postType;

  // check the types for documentation on how these work
  switch (elementRuleType) {
    case "section":
      return containerNestingRule;

    case "popup":
      return [...containerNestingRule, "section"];

    case "container":
      return containerNestingRule;

    case "container-link":
      return containerNestingRule.filter(
        type => type !== "final-link" && type !== "container-link"
      );

    case "container-excluding-self":
      return containerNestingRule.filter(
        type => type !== "container-excluding-self"
      );

    case "container-restricted":
      return [];

    case "final":
      return [];

    case "final-link":
      return [];

    case "final-that-is-allowed-inside-root":
      return [];

    case "root":
      if (
        documentPostType === getBreakdanceBlockPostType() ||
        documentPostType === "breakdance_acf_block"
      ) {
        return [...containerNestingRule, "section"];
      } else if (documentPostType === "breakdance_popup") {
        return ["popup"];
      } else {
        if (configStore.builderMode === "breakdance") {
          return ["section", "final-that-is-allowed-inside-root"];
        } else if (configStore.builderMode === "oxygen") {
          return [
            ...containerNestingRule,
            "section",
            "final-that-is-allowed-inside-root"
          ];
        } else {
          return [];
        }
      }
    default:
      return [];
  }
}

export function getDefaultTopLevelElementSlug(
  overrideDocumentPostType?: CustomDocumentPostTypes
) {
  const documentPostType =
    overrideDocumentPostType || documentStore.document.documentMeta.postType;

  if (documentPostType === "breakdance_popup") {
    return POPUP_SLUG;
  } else {
    return SECTION_SLUG;
  }
}

export function getTopLevelElementId(tree: Tree) {
  if (uiStore.inlineEditingContext?.elementId) {
    return uiStore.inlineEditingContext.elementId;
  }

  return tree.root.id;
}

export function getNestingRuleFromElementSlug(
  elementSlug: string
): BuilderElementNestingRule {
  const element = getElementFromSlug(elementSlug);

  if (
    uiStore.inlineEditingContext?.elementId &&
    element.nestingRule.inlineEditable
  ) {
    return {
      type: "container"
    };
  }

  return element.nestingRule;
}

export function getNestingRuleFromNode(
  node: TreeNode
): ElementNestingRuleAndChildrenRuleTypes {
  return {
    self: getElementNestingRuleFromNode(node),
    children: getNestingTypesFromNodeChildren(node)
  };
}

export function findNearestParentThatWillAcceptNodes(
  tree: Tree,
  potentialParentNode: TreeNode,
  nestingRuleForNodesToAdd: ElementNestingRuleAndChildrenRuleTypes
) {
  let allowed = false;
  let _potentialParentNode: TreeNode | undefined = potentialParentNode;

  while (_potentialParentNode) {
    allowed = isAllowedInside(
      tree,
      nestingRuleForNodesToAdd.self,
      nestingRuleForNodesToAdd.children,
      _potentialParentNode
    );

    if (allowed) break;

    _potentialParentNode = findNode(tree, _potentialParentNode._parentId);
  }

  return _potentialParentNode || false;
}

/**
 * *thisNode* is not required to be present in *tree*
 *
 * *thatNodeId* must be present in *tree*
 */
export function isThisNodeAllowedInsideThatNode(
  tree: Tree,
  thisNode: TreeNode,
  thatNodeId: TreeNode["id"],
  overrideDocumentPostType?: CustomDocumentPostTypes
) {
  const thatNode = findNode(tree, thatNodeId);
  if (!thatNode) return false;

  if (wouldNodeBeADescendantOfItself(tree, thisNode, thatNode.id)) {
    return false;
  }

  const thisNodeRule = getElementNestingRuleFromNode(thisNode);
  const thatNodeRule = getElementNestingRuleFromNode(thatNode);

  if (
    thisNodeRule.notAllowedWhenNodeTypeIsPresentInTree &&
    areNodeTypesPresentInTree(
      tree,
      thisNodeRule.notAllowedWhenNodeTypeIsPresentInTree
    )
  ) {
    return false;
  }

  if (
    !isNestingRuleAllowedInside(
      thisNodeRule,
      thatNodeRule,
      thatNode.data.type,
      overrideDocumentPostType
    )
  ) {
    return false;
  }

  // does the node or any of it's children have a type which is a banned descendant of the parent or any of its ancestors
  if (
    areNestingTypesForPotentialDescendantsBanned(
      [...getNestingTypesFromNodeChildren(thisNode), thisNodeRule.type],
      getBannedDescendantNestingTypesForNodeAndNodeAncestors(tree, thatNode)
    )
  ) {
    return false;
  }

  if (
    thisNodeRule.restrictedToBeADescendantOf &&
    !doDescendantRestrictionsAllowNestingInsideOfNode(
      tree,
      thatNode,
      thisNodeRule.restrictedToBeADescendantOf
    )
  ) {
    return false;
  }

  if (
    thisNodeRule.restrictedToBeADirectChildOf &&
    !thisNodeRule.restrictedToBeADirectChildOf.includes(thatNode.data.type)
  ) {
    return false;
  }

  return true;
}

export function getIndexToPlaceNode(
  tree: Tree,
  parentNode: TreeNode,
  activeNode: TreeNode
): number | false {
  let index: false | number;

  if (parentNode.id === findNodeParent(tree, activeNode)?.id) {
    index = getNodeIndex(tree, activeNode.id);
  } else {
    const childContainingActiveNode = findNodeThatIsAncestorOfNode(
      tree,
      parentNode.children,
      activeNode
    );
    index = childContainingActiveNode
      ? getNodeIndex(tree, childContainingActiveNode.id)
      : false;
  }

  if (index !== false) {
    index = index + 1;
  }

  return index;
}

export function findPlaceToAddNodes(
  tree: Tree,
  elementIdToAddNodesInsideOfOrNear: TreeNode["id"] | null,
  nestingRuleForNodesToAdd: ElementNestingRuleAndChildrenRuleTypes
): PlaceToAddElementInTree {
  const node =
    elementIdToAddNodesInsideOfOrNear === null
      ? undefined
      : findNode(tree, elementIdToAddNodesInsideOfOrNear);

  if (
    nestingRuleForNodesToAdd.self.notAllowedWhenNodeTypeIsPresentInTree &&
    areNodeTypesPresentInTree(
      tree,
      nestingRuleForNodesToAdd.self.notAllowedWhenNodeTypeIsPresentInTree
    )
  ) {
    return left("NOT_ALLOWED_BECAUSE_OF_OTHER_NODES");
  }

  // are the nodes allowed inside the node or any of its parents?
  if (node) {
    const nearestParentThatWillAcceptNodes =
      findNearestParentThatWillAcceptNodes(
        tree,
        node,
        nestingRuleForNodesToAdd
      );

    if (nearestParentThatWillAcceptNodes) {
      return right({
        index: getIndexToPlaceNode(
          tree,
          nearestParentThatWillAcceptNodes,
          node
        ),
        place: nearestParentThatWillAcceptNodes
      });
    }
  }

  // are the nodes allowed directly in the root?
  const allowedInRoot = isAllowedInside(
    tree,
    nestingRuleForNodesToAdd.self,
    nestingRuleForNodesToAdd.children,
    tree.root
  );

  if (allowedInRoot) {
    return right({
      index: false,
      place: tree.root
    });
  }

  // can it be added to the last child of root?
  if (tree.root.children.length > 0) {
    const lastChildOfRoot = tree.root.children[tree.root.children.length - 1];

    const allowedInLastChildOfRoot = isAllowedInside(
      tree,
      nestingRuleForNodesToAdd.self,
      nestingRuleForNodesToAdd.children,
      lastChildOfRoot
    );

    if (allowedInLastChildOfRoot)
      return right({ index: false, place: lastChildOfRoot });
  }

  // check to see if a section could be created in root and the element could be added to that section
  // but maybe this section shit should be in a different function
  const isDefaultTopLevelElementAllowedInsideRoot = isAllowedInside(
    tree,
    getElementFromSlug(getDefaultTopLevelElementSlug()).nestingRule,
    [],
    tree.root
  );

  const areNodesAllowedInsideDefaultTopLevelElement = (
    nestingTypesForNodesToAdd: ElementNestingRuleAndChildrenRuleTypes
  ) => {
    const DEFAULT_TOP_LEVEL_ELEMENT_SLUG = getDefaultTopLevelElementSlug();
    if (
      !isNestingRuleAllowedInside(
        nestingTypesForNodesToAdd.self,
        getElementFromSlug(DEFAULT_TOP_LEVEL_ELEMENT_SLUG).nestingRule,
        DEFAULT_TOP_LEVEL_ELEMENT_SLUG
      )
    ) {
      return false;
    }

    const bannedDescendantTypes = getBannedDescendantNestingTypes(
      getElementFromSlug(DEFAULT_TOP_LEVEL_ELEMENT_SLUG).nestingRule.type
    );

    if (
      areNestingTypesForPotentialDescendantsBanned(
        [
          nestingTypesForNodesToAdd.self.type,
          ...nestingTypesForNodesToAdd.children
        ],
        bannedDescendantTypes
      )
    ) {
      return false;
    }

    if (
      nestingTypesForNodesToAdd.self.restrictedToBeADescendantOf &&
      !nestingTypesForNodesToAdd.self.restrictedToBeADescendantOf.includes(
        DEFAULT_TOP_LEVEL_ELEMENT_SLUG
      )
    ) {
      return false;
    }

    if (
      nestingTypesForNodesToAdd.self.restrictedToBeADirectChildOf &&
      !nestingTypesForNodesToAdd.self.restrictedToBeADirectChildOf.includes(
        DEFAULT_TOP_LEVEL_ELEMENT_SLUG
      )
    ) {
      return false;
    }

    return true;
  };

  if (
    areNodesAllowedInsideDefaultTopLevelElement(nestingRuleForNodesToAdd) &&
    isDefaultTopLevelElementAllowedInsideRoot
  ) {
    return right("TOP_LEVEL");
  }

  return left("NONE");
}

export function isElementRestrictedToBeChildOrDescendantOfSlug(
  element: BuilderElement,
  slug: string
) {
  if (!element || !element.nestingRule) return false;

  if (
    element.nestingRule.restrictedToBeADescendantOf &&
    element.nestingRule.restrictedToBeADescendantOf.includes(slug)
  ) {
    return true;
  }

  if (
    element.nestingRule.restrictedToBeADirectChildOf &&
    element.nestingRule.restrictedToBeADirectChildOf.includes(slug)
  ) {
    return true;
  }

  return false;
}

export function isElementInsideRestrictedAncestor(
  node: TreeNode,
  element: BuilderElement
) {
  return parentNodesReduce(
    (node, acc) => {
      if (
        isElementRestrictedToBeChildOrDescendantOfSlug(element, node.data.type)
      ) {
        acc = true;
      }

      return acc;
    },
    documentStore.document.tree,
    node,
    false
  );
}
